import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { pluralise } from "simplydo/core/helpers";
import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import {
  type OnCompleteParams,
  allowedImageTypesList,
} from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { cx, styled } from "simplydo/react/styled";
import { Button, Card, Icon, Modal, Segment } from "simplydo/react/ui";

import { ImageChooser } from "components/lib/Choosers";

import type { ChooserGalleryImage } from "./types";

const imageHeight = 250;
const fullImageHeight = 365;

const GalleryImages = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
`;

const GalleryThumbnail = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  margin: 3px;
  object-fit: cover;
  cursor: pointer;

  &.is-active {
    border: 3px solid ${({ theme }) => theme.primaryColour};
  }
`;

const NoActiveImage = styled.div<{ $imageHeight: string; $imageWidth: string }>`
  border-radius: 10px;
  height: ${({ $imageHeight }) => $imageHeight};
  width: ${({ $imageWidth }) => $imageWidth};
  border: 1px solid lightgray;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 3px;
`;

type GalleryProps = {
  images: ChooserGalleryImage[];
  getFileUrl?: (file: ChooserGalleryImage) => string;
  onRemove?: (fileId: string) => void;
};

const Gallery = ({ images, getFileUrl, onRemove }: GalleryProps) => {
  const { t } = useTranslation();

  const [activeImage, setActiveImage] = useState<ChooserGalleryImage | null>(
    null,
  );
  const [imageModalOpen, setImageModalOpen] = useState(false);

  useEffect(() => {
    if (images?.length) {
      setActiveImage(images[0] ?? null);
    } else {
      setActiveImage(null);
    }
  }, [images]);

  const handleEnterPress = useCallback((event: KeyboardEvent) => {
    if (event.keyCode === 13) {
      setImageModalOpen(true);
    }
  }, []);

  return (
    <>
      <Card.Group centered onClick={(e: MouseEvent) => e.stopPropagation()}>
        <Card style={{ width: "80%" }}>
          <Card.Content style={{ color: "#808080" }}>
            <Icon name="images outline" />
            {pluralise(images.length, "uploaded image", "uploaded images")}
          </Card.Content>
          <Card.Content textAlign="center">
            {activeImage ? (
              <div style={{ margin: 3, flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    backgroundColor: "#f8f8f8",
                    borderRadius: 15,
                  }}
                >
                  <img
                    tabIndex={0}
                    src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                    alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                    onClick={() => {
                      setImageModalOpen(true);
                    }}
                    onKeyDown={(e) => handleEnterPress(e)}
                    style={{
                      height: fullImageHeight,
                      width: "100%",
                      objectFit: "contain",
                      borderRadius: 10,
                      cursor: "default",
                    }}
                  />
                </div>
              </div>
            ) : (
              <NoActiveImage
                $imageHeight={`${imageHeight + 40}px`}
                $imageWidth={"100%"}
              >
                <h5>
                  {!images.length
                    ? t("multiimage.upload.none")
                    : "Select an image"}
                </h5>
              </NoActiveImage>
            )}
            <GalleryImages>
              {images && Array.isArray(images) && images.length
                ? images.map((s) => (
                    <GalleryThumbnail
                      src={getFileUrl ? getFileUrl(s) : s.url}
                      key={s._id}
                      onClick={() => setActiveImage(s)}
                      className={cx(activeImage?._id === s._id && "is-active")}
                    />
                  ))
                : null}
            </GalleryImages>
          </Card.Content>
          {onRemove ? (
            <Card.Content
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "12px 16px",
              }}
            >
              <Button
                basic
                icon="trash"
                onClick={() => {
                  if (activeImage) {
                    onRemove(activeImage._id);
                  }
                }}
                size="tiny"
                style={{ marginLeft: 0, marginRight: 0 }}
              />
            </Card.Content>
          ) : null}
        </Card>
      </Card.Group>
      {activeImage ? (
        <Modal
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          style={{ maxWidth: "90vw", maxHeight: "90vh" }}
        >
          <Modal.Header> {activeImage.name} </Modal.Header>
          <Modal.Content
            scrolling
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 0,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {activeImage && (
              <img
                src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                style={{
                  maxWidth: "90%",
                  maxHeight: "60%",
                  objectFit: "contain",
                  marginBottom: 10,
                  marginTop: 10,
                }}
              />
            )}
          </Modal.Content>
          <Modal.Actions>
            {activeImage && (
              <a
                href={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                download={activeImage.name || "image.png"}
              >
                <Button primary content="Download image" icon="download" />
              </a>
            )}
            <Button
              content="Close"
              onClick={() => {
                setImageModalOpen(false);
              }}
            />
          </Modal.Actions>
        </Modal>
      ) : null}
    </>
  );
};

type DropZoneProps = {
  children: import("react").ReactNode;
  getFileUrl: (file: ChooserGalleryImage) => string;
  images: Array<{ _id: string; name: string; value: string; url: string }>;
  onRemove: (fileId: string) => void;
  onClick: () => void;
};

const DropZone = ({
  children,
  images,
  getFileUrl,
  onClick,
  onRemove,
}: DropZoneProps) => {
  return (
    <Segment placeholder onClick={onClick}>
      {children}
      {images?.length > 0 ? (
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <Gallery
            images={images}
            getFileUrl={getFileUrl}
            onRemove={onRemove}
          />
        </div>
      ) : null}
    </Segment>
  );
};

type MultiImageChooserProps = {
  images: Array<{ _id: string; name: string; value: string; url: string }>;
  onComplete: (files: OnCompleteParams[]) => void;
  deleteFile: (fileId: string) => void;
  getFileUrl?: (file: ChooserGalleryImage) => string;
  forType: string;
  forId: string;
  editing?: boolean;
  maxImages?: number;
  showGallery?: boolean;
};

const MultiImageChooser = ({
  images = [],
  onComplete,
  deleteFile,
  forType,
  forId,
  editing,
  getFileUrl,
  maxImages,
  showGallery = true,
}: MultiImageChooserProps) => {
  if (!editing && !images.length) return null;

  if (!editing) {
    return <Gallery images={images} getFileUrl={getFileUrl} />;
  }

  const MultiImageDropzone = ({
    children,
    onClick,
  }: {
    children: ReactNode;
    onClick: () => void;
  }) => (
    <DropZone
      images={images}
      getFileUrl={getFileUrl ?? ((file) => file.url)}
      onClick={onClick}
      onRemove={deleteFile}
    >
      {children}
    </DropZone>
  );

  const onSingleComplete = (
    responseFilename: string,
    filename: string,
    responseDownload: string,
  ) => {
    onComplete([
      {
        savedName: responseFilename,
        fileName: filename,
        signedDownloadRequest: responseDownload,
      },
    ]);
  };

  return (
    <ImageChooser
      allowMultiple={true}
      onComplete={onSingleComplete}
      onCompleteMultiple={onComplete}
      forType={forType}
      forId={forId}
      trigger={
        <FileChooserDropzoneTrigger
          forType={forType}
          forId={forId}
          accept={allowedImageTypesList.join(",")}
          allowMultiple={true}
          onComplete={onSingleComplete}
          onCompleteMultiple={onComplete}
          dropzoneProps={{
            title: "Upload multiple images",
            subtitle: maxImages
              ? `Maximum of ${pluralise(maxImages, "image", "images")}`
              : undefined,
            DropZoneComponent: showGallery ? MultiImageDropzone : Segment,
            defaultIcon: "images",
          }}
        />
      }
    />
  );
};

export default MultiImageChooser;
